import React, { useEffect, useState } from "react";
import { Search, X, RotateCcw } from "lucide-react";
import "./ui.scss";

// --- Status chips (All / Pending / ...) with live counts -------------------
export const StatusTabs = ({
  tabs,
  value,
  onChange,
  label = "Type",
  ariaLabel,
}) => (
  <div className="hr-field hr-field--tabs">
    <span className="hr-field__label" id="hr-status-label">
      {label}
    </span>
    <div
      className="hr-filter-bar__tabs"
      role="group"
      {...(ariaLabel
        ? { "aria-label": ariaLabel }
        : { "aria-labelledby": "hr-status-label" })}
    >
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`hr-chip ${value === tab.key ? "is-active" : ""}`}
          aria-pressed={value === tab.key}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {typeof tab.count === "number" ? ` (${tab.count})` : ""}
        </button>
      ))}
    </div>
  </div>
);

// --- Debounced search ------------------------------------------------------
export const SearchInput = ({
  value,
  onChange,
  placeholder = "Search...",
  label = "Search",
  delay = 250,
}) => {
  const [draft, setDraft] = useState(value);

  // Keep the box in sync when the parent changes the value (e.g. Reset).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Push the draft upward after the user stops typing.
  useEffect(() => {
    if (draft === value) return undefined;
    const timer = setTimeout(() => onChange(draft), delay);
    return () => clearTimeout(timer);
  }, [draft, value, onChange, delay]);

  return (
    <div className="hr-field hr-field--grow">
      <label className="hr-field__label" htmlFor="hr-search-input">
        {label}
      </label>
      <div className="hr-search">
        <Search className="hr-search__icon" size={16} aria-hidden="true" />
        <input
          id="hr-search-input"
          type="text"
          className="hr-input"
          placeholder={placeholder}
          autoComplete="off"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        {draft && (
          <button
            type="button"
            className="hr-search__clear"
            aria-label="Clear search"
            onClick={() => {
              setDraft("");
              onChange("");
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

// --- Native select ---------------------------------------------------------
export const SelectDropdown = ({ id, label, value, onChange, options }) => (
  <div className="hr-field">
    <label className="hr-field__label" htmlFor={id}>
      {label}
    </label>
    <select
      id={id}
      className="hr-select__control"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

// --- Date range (two native date inputs, no extra dependency) ---------------
export const DateRangeFilter = ({ from, to, onChange }) => (
  <div className="hr-field">
    <span className="hr-field__label" id="hr-daterange-label">
      Date range
    </span>
    <div
      className="hr-daterange"
      role="group"
      aria-labelledby="hr-daterange-label"
    >
      <input
        type="date"
        className="hr-input"
        aria-label="From date"
        value={from}
        max={to || undefined}
        onChange={(e) => onChange({ from: e.target.value, to })}
      />
      <span className="hr-daterange__sep" aria-hidden="true">
        –
      </span>
      <input
        type="date"
        className="hr-input"
        aria-label="To date"
        value={to}
        min={from || undefined}
        onChange={(e) => onChange({ from, to: e.target.value })}
      />
    </div>
  </div>
);

// --- Wrapper card ----------------------------------------------------------
export const FilterToolbar = ({
  tabs,
  children,
  onReset,
  resetDisabled = false,
}) => (
  <section className="hr-card hr-filter-bar" aria-label="Filters">
    {tabs}
    <div className="hr-filter-bar__fields">
      {children}
      <button
        type="button"
        className="hr-btn hr-btn--secondary"
        onClick={onReset}
        disabled={resetDisabled}
      >
        <RotateCcw size={15} aria-hidden="true" />
        Reset
      </button>
    </div>
  </section>
);
